'use client';

import { swissCantonLabel } from '@/lib/countries';
import { COMPANY_COUNTRIES, countryScopeOptions, isCompanyCountryCode } from '@/lib/countries';
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import {
  type CellContext,
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Building2,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { Locale } from '@/lib/i18n-routes';
import type { InsolvencyListItem } from '@/lib/backend-api';
import {
  SWISS_CANTON_NAMES,
  tryBuildCompanySlugFromCanonical,
  type CompanyCountryCode,
} from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import { useCompanyLogos } from '@/hooks/use-company-logos';
import { Button } from '@/components/ui/button';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { Input } from '@/components/ui/input';
import { LocaleLink } from '@/components/locale-link';
import { FeedAlertTrigger } from '@/new_components/alerts/FeedAlertTrigger';
import { CountryScopeControl } from '@/new_components/shared/CountryScopeControl';
import { FilterSidebarPanel } from '@/new_components/searchresults/FilterSidebarPanel';
import {
  FilterChipBar,
  type FilterChip,
} from '@/new_components/searchresults/FilterChipBar';
import {
  FilterLabel,
  FilterCheckbox,
  FIELD_LABEL_CLASS,
  INPUT_PLACEHOLDER_CLASS,
} from '@/new_components/searchresults/FilterControls';
import { FilterValuePicker } from '@/new_components/searchresults/FilterValuePicker';
import {
  CATEGORY_TO_DIVISIONS,
  naceToCategory,
  getCategoryLabel,
  allIndustryCategoriesSorted,
} from '@/lib/industry-categories';

const secondaryTextClass = 'text-sm text-muted-foreground dark:text-zinc-300';
const COUNTRY_STORAGE_KEY = 'fd_insolvency_country';

function isInsolvencyCountry(value: unknown): value is CompanyCountryCode {
  return isCompanyCountryCode(value) && COMPANY_COUNTRIES[value].insolvencyScope;
}

// Insolvency type options per country. The `value` is the exact DB string in
// company_insolvency.category, sent verbatim to the backend: the German
// InsolvencyCategory enum (scraper.py) for DE, the SHAB notice kinds for CH,
// the Firmenbuch insolvency facts for AT (backend/firmenbuch/ingest.py).
const INSOLVENCY_CATEGORY_OPTIONS: Record<
  CompanyCountryCode,
  { value: string; label_de: string; label_en: string }[]
> = {
  DE: [
    { value: 'Eröffnungen',             label_de: 'Eröffnung',                label_en: 'Opening' },
    { value: 'Sicherungsmaßnahmen',     label_de: 'Sicherungsmaßnahmen',      label_en: 'Protective measures' },
    { value: 'Abweisungen mangels Masse', label_de: 'Abweisung mangels Masse', label_en: 'Dismissal for lack of assets' },
  ],
  CH: [
    { value: 'Konkurseröffnung',               label_de: 'Konkurseröffnung',               label_en: 'Bankruptcy opened' },
    { value: 'Einstellung mangels Aktiven',    label_de: 'Einstellung mangels Aktiven',    label_en: 'Proceedings suspended for lack of assets' },
    { value: 'Provisorische Nachlassstundung', label_de: 'Provisorische Nachlassstundung', label_en: 'Provisional debt-restructuring moratorium' },
    { value: 'Definitive Nachlassstundung',    label_de: 'Definitive Nachlassstundung',    label_en: 'Definitive debt-restructuring moratorium' },
  ],
  AT: [
    { value: 'Konkursverfahren eröffnet',        label_de: 'Konkursverfahren eröffnet',        label_en: 'Bankruptcy proceedings opened' },
    { value: 'Sanierungsverfahren eröffnet',     label_de: 'Sanierungsverfahren eröffnet',     label_en: 'Restructuring proceedings opened' },
    { value: 'Nichteröffnung mangels Vermögens', label_de: 'Nichteröffnung mangels Vermögens', label_en: 'Not opened for lack of assets' },
  ],
};

const INSOLVENCY_CATEGORY_LABEL_KEYS = new Map<string, string>([
  ['Eröffnungen', 'pages.insolvenzticker.shortProcedure.opened'],
  ['Sicherungsmaßnahmen', 'pages.insolvenzticker.shortProcedure.protective'],
  ['Abweisungen mangels Masse', 'pages.insolvenzticker.shortProcedure.dismissed'],
  ['Konkurseröffnung', 'pages.insolvenzticker.shortProcedure.bankruptcy'],
  ['Einstellung mangels Aktiven', 'pages.insolvenzticker.shortProcedure.discontinued'],
  ['Definitive Nachlassstundung', 'pages.insolvenzticker.shortProcedure.moratoriumFinal'],
  ['Provisorische Nachlassstundung', 'pages.insolvenzticker.shortProcedure.moratoriumProvisional'],
  ['Konkursverfahren eröffnet', 'pages.insolvenzticker.shortProcedure.bankruptcyOpened'],
  ['Sanierungsverfahren eröffnet', 'pages.insolvenzticker.shortProcedure.restructuringOpened'],
  ['Nichteröffnung mangels Vermögens', 'pages.insolvenzticker.shortProcedure.notOpened'],
]);

// Revenue buckets of the ticker as one [min, max] range for the alert. The
// feed takes a single range, so non-adjacent buckets widen to their hull; an
// open-ended bucket leaves that side open. All four (or none) = no filter.
const REVENUE_BUCKET_BOUNDS: Record<string, [number | null, number | null]> = {
  lt500k: [null, 500000],
  '500kto5': [500000, 5000000],
  '5to10': [5000000, 10000000],
  gt10: [10000000, null],
};

function revenueBounds(selected: Set<string>): {
  min: number | null;
  max: number | null;
} {
  const bounds = Array.from(selected)
    .map((key) => REVENUE_BUCKET_BOUNDS[key])
    .filter((b): b is [number | null, number | null] => !!b);
  if (bounds.length === 0 || bounds.length === 4) return { min: null, max: null };
  const mins = bounds.map((b) => b[0]);
  const maxs = bounds.map((b) => b[1]);
  return {
    min: mins.includes(null) ? null : Math.min(...(mins as number[])),
    max: maxs.includes(null) ? null : Math.max(...(maxs as number[])),
  };
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function InsolvenztickerClient() {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const lang = i18n.language;
  const cantonOptions = useMemo(
    () =>
      Object.keys(SWISS_CANTON_NAMES)
        .map((code) => ({ value: code, label: swissCantonLabel(code, lang) }))
        .sort((a, b) => a.label.localeCompare(b.label, lang)),
    [lang],
  );
  const fmt = useMemo(() => createFormatters(lang), [lang]);

  // Data
  const [items, setItems] = useState<InsolvencyListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters start empty so SSR and hydration render identical HTML; the URL
  // params and the stored country are read in a useEffect below so deep
  // links still land on a pre-filtered view after hydration.
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // DE: German insolvency notices (region = Bundesland); CH: SHAB notices
  // (region = canton, held as its two-letter code); AT: Firmenbuch register
  // facts (region = Austrian Bundesland name).
  const [country, setCountry] = useState<CompanyCountryCode>('DE');
  const [industryFilters, setIndustryFilters] = useState<Set<string>>(new Set());
  const [bundeslandFilters, setBundeslandFilters] = useState<Set<string>>(
    new Set(),
  );
  const [cantonFilters, setCantonFilters] = useState<Set<string>>(new Set());
  const [revenueFilters, setRevenueFilters] = useState<Set<string>>(new Set());
  // Free-form revenue min/max from URL — used when no preset bucket matches
  // (e.g. the homepage Insolvency demo links here with revenue_min=100000).
  const [revenueMinOverride, setRevenueMinOverride] = useState<number | null>(
    null,
  );
  const [revenueMaxOverride, setRevenueMaxOverride] = useState<number | null>(
    null,
  );
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(new Set());

  // Hydrate filter state from URL params after mount. Supported keys:
  //   ?country=CH&search=…&bundesland=…&canton=ZH&nace_divisions=41,42&insolvency_categories=Eröffnungen,Sicherungsmaßnahmen&revenue_ranges=…
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const urlCountry = sp.get('country')?.toUpperCase();
    let initialCountry: CompanyCountryCode = 'DE';
    if (sp.has('country')) {
      if (isInsolvencyCountry(urlCountry)) initialCountry = urlCountry;
    } else {
      try {
        const storedCountry = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
        if (isInsolvencyCountry(storedCountry)) initialCountry = storedCountry;
      } catch {
        // Storage may be unavailable; keep the default country.
      }
    }
    setCountry(initialCountry);
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, initialCountry);
    } catch {
      // The country picker works even when storage is unavailable.
    }
    const search = sp.get('search') ?? '';
    if (search) {
      setSearchInput(search);
      setSearchQuery(search);
    }
    const bundesland = sp.get('bundesland');
    if (bundesland) setBundeslandFilters(new Set([bundesland]));
    const canton = sp.get('canton')?.toUpperCase();
    if (canton && SWISS_CANTON_NAMES[canton]) setCantonFilters(new Set([canton]));
    const naceDivs = (sp.get('nace_divisions') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (initialCountry !== 'CH' && naceDivs.length > 0) {
      const industrySet = new Set<string>();
      for (const div of naceDivs) {
        const cat = naceToCategory(div);
        if (cat) industrySet.add(cat);
      }
      if (industrySet.size > 0) setIndustryFilters(industrySet);
    }
    const cats = (sp.get('insolvency_categories') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (cats.length > 0) setCategoryFilters(new Set(cats));
    const revs = (sp.get('revenue_ranges') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (initialCountry !== 'CH' && revs.length > 0) setRevenueFilters(new Set(revs));
    const rMin = parseFloat(sp.get('revenue_min') ?? '');
    if (initialCountry !== 'CH' && !Number.isNaN(rMin)) setRevenueMinOverride(rMin);
    const rMax = parseFloat(sp.get('revenue_max') ?? '');
    if (initialCountry !== 'CH' && !Number.isNaN(rMax)) setRevenueMaxOverride(rMax);
    // Mount-only: URL drives initial state; subsequent changes come from UI.
  }, []);

  const insolvencyCategoryOptions = INSOLVENCY_CATEGORY_OPTIONS[country] ?? [];
  // The region filter in force: Bundesland names for DE / AT, canton codes
  // for CH.
  const regionFilters = country === 'CH' ? cantonFilters : bundeslandFilters;

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Pagination
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);

  const logEvent = useCallback((event: string, description: string, meta?: Record<string, unknown>) => {
    fetch('/api/log/page-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        entityType: 'insolvenzticker',
        description,
        url: window.location.pathname,
        ...(meta ? { meta: JSON.stringify(meta) } : {}),
      }),
      keepalive: true,
    }).catch(() => {});
  }, []);

  // Build query params from filters
  const buildQueryParams = useCallback((offset: number) => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    // Collect all NACE divisions for selected industry categories
    if (country !== 'CH' && industryFilters.size > 0) {
      const divisions = Array.from(industryFilters).flatMap((k) => CATEGORY_TO_DIVISIONS[k] ?? []);
      if (divisions.length > 0) params.set('nace_divisions', divisions.join(','));
    }
    if (categoryFilters.size > 0) {
      params.set('insolvency_categories', Array.from(categoryFilters).join(','));
    }
    // DE is the backend default, so DE requests carry no `country`; every
    // other country must send it or it gets German notices. Only one
    // bundesland / canton supported by backend — pick first.
    if (country !== 'DE') params.set('country', country);
    if (country === 'CH') {
      if (cantonFilters.size > 0) {
        params.set('canton', Array.from(cantonFilters)[0]);
      }
    } else if (bundeslandFilters.size > 0) {
      params.set('bundesland', Array.from(bundeslandFilters)[0]);
    }
    // Revenue ranges — sent as OR-of-buckets via revenue_ranges. Each bucket
    // is "min:max" with either side optional for open-ended.
    if (country !== 'CH' && revenueFilters.size > 0 && revenueFilters.size < 4) {
      const buckets: string[] = [];
      // "null" matches NULL revenue, included in the smallest bucket.
      if (revenueFilters.has('lt500k')) buckets.push('null', ':500000');
      if (revenueFilters.has('500kto5')) buckets.push('500000:5000000');
      if (revenueFilters.has('5to10')) buckets.push('5000000:10000000');
      if (revenueFilters.has('gt10')) buckets.push('10000000:');
      if (buckets.length > 0) params.set('revenue_ranges', buckets.join(','));
    }
    // size === 4: all selected → no filter (entire range)
    if (country !== 'CH' && revenueMinOverride !== null) {
      params.set('revenue_min', String(revenueMinOverride));
    }
    if (country !== 'CH' && revenueMaxOverride !== null) {
      params.set('revenue_max', String(revenueMaxOverride));
    }
    return params.toString();
  }, [
    searchQuery,
    country,
    industryFilters,
    bundeslandFilters,
    cantonFilters,
    revenueFilters,
    revenueMinOverride,
    revenueMaxOverride,
    categoryFilters,
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQueryParams(page * PAGE_SIZE);
      const res = await fetch(`/api/insolvency?${qs}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      setItems(json.data?.items ?? []);
      setTotal(json.data?.total ?? 0);
    } catch {
      setError(t('pages.insolvenzticker.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, page, buildQueryParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  const prevFiltersRef = useRef('');
  useEffect(() => {
    const key = `${country}|${searchQuery}|${[...industryFilters].join(',')}|${[...bundeslandFilters].join(',')}|${[...cantonFilters].join(',')}|${[...revenueFilters].join(',')}|${[...categoryFilters].join(',')}`;
    if (prevFiltersRef.current && prevFiltersRef.current !== key) {
      setPage(0);
    }
    prevFiltersRef.current = key;
  }, [country, searchQuery, industryFilters, bundeslandFilters, cantonFilters, revenueFilters, categoryFilters]);

  const allIndustryCategories = useMemo(() => allIndustryCategoriesSorted(lang), [lang]);

  const toggleFilter = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, value: string, filterName?: string) => {
    setter((prev) => {
      const next = new Set(prev);
      const action = next.has(value) ? 'remove' : 'add';
      if (action === 'remove') next.delete(value);
      else next.add(value);
      logEvent('filter_change', `${action} ${filterName ?? 'filter'}: ${value}`, { filter: filterName, value, action });
      return next;
    });
  };

  // Countries have different region and category vocabularies, so a
  // switch clears both filters. The choice is mirrored into ?country= (and the
  // cleared params dropped) so a reload or shared link lands on the same view.
  const handleCountryChange = (next: CompanyCountryCode) => {
    if (next === country) return;
    setCountry(next);
    setBundeslandFilters(new Set());
    setCantonFilters(new Set());
    setCategoryFilters(new Set());
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, next);
    } catch {
      // The country picker works even when storage is unavailable.
    }
    const sp = new URLSearchParams(window.location.search);
    if (next !== 'DE') sp.set('country', next);
    else sp.delete('country');
    sp.delete('bundesland');
    sp.delete('canton');
    sp.delete('insolvency_categories');
    if (next === 'CH') {
      setIndustryFilters(new Set());
      setRevenueFilters(new Set());
      setRevenueMinOverride(null);
      setRevenueMaxOverride(null);
      sp.delete('nace_divisions');
      sp.delete('revenue_ranges');
      sp.delete('revenue_min');
      sp.delete('revenue_max');
    }
    const qs = sp.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
    logEvent('filter_change', `country: ${next}`, { filter: 'country', value: next });
  };

  const [filtersOpen, setFiltersOpen] = useState(false);

  // The page's filters in the insolvencies feed shape (backend
  // `backend/alerts/feeds`): regions are what the ticker sends as its region
  // filter (Bundesland names / canton codes), categories the exact DB
  // Verfahrensart strings. The free-text name search has no feed equivalent
  // and is not part of the alert.
  const alertFilters = useMemo(() => {
    const filters: Record<string, unknown> = { country };
    if (regionFilters.size > 0) filters.regions = Array.from(regionFilters);
    if (categoryFilters.size > 0) filters.categories = Array.from(categoryFilters);
    if (country !== 'CH') {
      if (industryFilters.size > 0) filters.industries = Array.from(industryFilters);
      const range = revenueBounds(revenueFilters);
      const revenueMin = revenueMinOverride ?? range.min;
      const revenueMax = revenueMaxOverride ?? range.max;
      if (revenueMin !== null) filters.revenue_min = revenueMin;
      if (revenueMax !== null) filters.revenue_max = revenueMax;
    }
    return filters;
  }, [
    country,
    regionFilters,
    categoryFilters,
    industryFilters,
    revenueFilters,
    revenueMinOverride,
    revenueMaxOverride,
  ]);
  const alertUnfiltered = Object.keys(alertFilters).length === 1;

  const revenueOptions = [
    { value: 'lt500k', label: t('pages.insolvenzticker.revenueUnder500k') },
    { value: '500kto5', label: t('pages.insolvenzticker.revenue500kTo5m') },
    { value: '5to10', label: t('pages.insolvenzticker.revenue5mTo10m') },
    { value: 'gt10', label: t('pages.insolvenzticker.revenueOver10m') },
  ];
  const categoryOptions = insolvencyCategoryOptions.map((option) => ({
    value: option.value,
    label: lang === 'de' ? option.label_de : option.label_en,
  }));
  const regionOptions =
    country === 'CH'
      ? cantonOptions
      : COMPANY_COUNTRIES[country].regions.map((value) => ({ value, label: value }));
  const removeSelected = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string,
  ) => {
    setter((previous) => {
      const next = new Set(previous);
      next.delete(value);
      return next;
    });
  };
  const regionSetter =
    country === 'CH' ? setCantonFilters : setBundeslandFilters;
  const regionLabel = t(
    country === 'CH'
      ? 'pages.insolvenzticker.canton'
      : 'pages.insolvenzticker.bundesland',
  );
  const chips: FilterChip[] = [];
  if (searchInput.trim())
    chips.push({
      key: 'search',
      label: searchInput,
      onRemove: () => setSearchInput(''),
    });
  for (const [key, selected, options, setter] of [
    ['category', categoryFilters, categoryOptions, setCategoryFilters],
    ['region', regionFilters, regionOptions, regionSetter],
    [
      'industry',
      industryFilters,
      allIndustryCategories.map((option) => ({
        value: option.key,
        label: option.label,
      })),
      setIndustryFilters,
    ],
    ['revenue', revenueFilters, revenueOptions, setRevenueFilters],
  ] as const) {
    if (country === 'CH' && (key === 'industry' || key === 'revenue')) continue;
    for (const value of selected)
      chips.push({
        key: `${key}:${value}`,
        label: options.find((option) => option.value === value)?.label ?? value,
        onRemove: () => removeSelected(setter, value),
      });
  }
  if (country !== 'CH' && (revenueMinOverride !== null || revenueMaxOverride !== null))
    chips.push({
      key: 'revenueRange',
      label: t('pages.insolvenzticker.revenueRange', {
        min:
          revenueMinOverride === null
            ? '–'
            : fmt.fullCurrency.format(revenueMinOverride),
        max:
          revenueMaxOverride === null
            ? '–'
            : fmt.fullCurrency.format(revenueMaxOverride),
      }),
      onRemove: () => {
        setRevenueMinOverride(null);
        setRevenueMaxOverride(null);
      },
    });
  const activeFilterCount = chips.length;
  useEffect(() => {
    const onOpen = () => setFiltersOpen(true);
    window.addEventListener('firmendata:open-filters', onOpen);
    return () => window.removeEventListener('firmendata:open-filters', onOpen);
  }, []);
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('firmendata:filter-count', { detail: activeFilterCount }),
    );
  }, [activeFilterCount]);

  const clearAllFilters = () => {
    setSearchInput('');
    setIndustryFilters(new Set());
    setCategoryFilters(new Set());
    setBundeslandFilters(new Set());
    setCantonFilters(new Set());
    setRevenueFilters(new Set());
    setRevenueMinOverride(null);
    setRevenueMaxOverride(null);
  };

  return (
    <div className="search-warm-bg relative lg:fixed lg:left-[var(--app-sidebar-width,0px)] lg:right-0 lg:w-[calc(100%-var(--app-sidebar-width,0px))] lg:top-[var(--header-height,95px)] lg:bottom-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px))] flex flex-col lg:flex-row min-h-0 w-full max-w-full lg:overflow-hidden bg-background">
      <FilterSidebarPanel
        id="ticker-filters"
        ariaLabel={t('search.filters')}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        footer={
          <Button className="w-full h-11" onClick={() => setFiltersOpen(false)}>
            {t('search.showFilterResults', { count: total.toLocaleString(locale) })}
          </Button>
        }
      >
        <div className="space-y-4">
          <CountryScopeControl
            value={country}
            options={countryScopeOptions('insolvencyScope')}
            onChange={(next) => {
              if (isInsolvencyCountry(next)) handleCountryChange(next);
            }}
          />
          <div className="space-y-1.5">
            <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
              {t('tenders.search')}
            </FilterLabel>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t(
                  // CH and AT notices carry no Aktenzeichen to search by.
                  country === 'DE'
                    ? 'pages.insolvenzticker.searchPlaceholder'
                    : 'pages.insolvenzticker.searchPlaceholderCH',
                )}
                className={`h-11 lg:h-9 text-base lg:text-sm pl-8 pr-11 ${INPUT_PLACEHOLDER_CLASS}`}
              />
              {searchInput && (
                <button
                  type="button"
                  aria-label={t('search.activeFilters.clearSearch')}
                  onClick={() => setSearchInput('')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 size-11 lg:size-9 flex items-center justify-center"
                >
                  <X className="size-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
              {t('pages.insolvenzticker.procedureType')}
            </FilterLabel>
            {categoryOptions.map((option) => (
              <FilterCheckbox
                key={option.value}
                lock={null}
                checked={categoryFilters.has(option.value)}
                label={option.label}
                onCheckedChange={() =>
                  toggleFilter(
                    setCategoryFilters,
                    option.value,
                    'insolvency_category',
                  )
                }
              />
            ))}
          </div>
          <div className="space-y-1.5">
            <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
              {regionLabel}
            </FilterLabel>
            <FilterValuePicker
              label={regionLabel}
              options={regionOptions}
              selected={[...regionFilters]}
              onToggle={(value) =>
                toggleFilter(
                  regionSetter,
                  value,
                  country === 'CH' ? 'canton' : 'bundesland',
                )
              }
              onRemove={(value) => removeSelected(regionSetter, value)}
            />
          </div>
          {country !== 'CH' && (
            <>
              <div className="space-y-1.5">
                <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
                  {t('pages.insolvenzticker.industry')}
                </FilterLabel>
                <FilterValuePicker
                  label={t('pages.insolvenzticker.industry')}
                  options={allIndustryCategories.map((option) => ({
                    value: option.key,
                    label: option.label,
                  }))}
                  selected={[...industryFilters]}
                  onToggle={(value) =>
                    toggleFilter(setIndustryFilters, value, 'industry')
                  }
                  onRemove={(value) => removeSelected(setIndustryFilters, value)}
                />
              </div>
              <div className="space-y-1.5">
                <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
                  {t('pages.insolvenzticker.revenue')}
                </FilterLabel>
                {revenueOptions.map((option) => (
                  <FilterCheckbox
                    key={option.value}
                    lock={null}
                    label={option.label}
                    checked={revenueFilters.has(option.value)}
                    onCheckedChange={() =>
                      toggleFilter(setRevenueFilters, option.value, 'revenue')
                    }
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </FilterSidebarPanel>
      <main className="lg:flex-1 min-w-0 flex flex-col relative lg:overflow-hidden">
        <div className="px-4 pt-4 pb-3 border-b shrink-0">
          <h1 className="text-lg lg:text-xl font-semibold">
            {t('pages.insolvenzticker.title')}
          </h1>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1 lg:line-clamp-2">
            {t('pages.insolvenzticker.intro')}
          </p>
          <div className="flex items-center gap-1.5 mt-3">
            <FeedAlertTrigger
              feedType="insolvencies"
              filters={alertFilters}
              isUnfiltered={alertUnfiltered}
              unfilteredDescription={t('alerts.trigger.unfilteredInsolvencies')}
              onAdjustFilters={() => {
                setFiltersOpen(true);
                window.setTimeout(
                  () => document.getElementById('ticker-filters')?.focus(),
                  0,
                );
              }}
              signInLabel={t('pages.insolvenzticker.signInToSubscribe')}
              onSignIn={() => {
                logEvent('login_click', 'From insolvency alert subscription');
                const returnTo = window.location.pathname + window.location.search;
                window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
              }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {t('pages.insolvenzticker.matchingAlerts')}{' '}
            <LocaleLink
              href="/contact?subject=Insolvenzen%20CRM%20Integration"
              className="text-primary hover:underline"
              onClick={() =>
                logEvent(
                  'crm_integration_click',
                  'Clicked CRM integration link',
                )
              }
            >
              {t('pages.insolvenzticker.crmIntegration')}
            </LocaleLink>
          </p>
        </div>
        <FilterChipBar
          chips={chips}
          onClearAll={clearAllFilters}
          className="[&_button]:min-h-11 [&_button]:min-w-11 lg:[&_button]:min-h-0 lg:[&_button]:min-w-0"
        />
        <div className="flex-1 min-h-0 min-w-0 lg:overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center min-h-[40vh]">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && (
            <div className="text-center text-destructive py-12">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
              <Search className="size-10 opacity-30" />
              <p className="text-sm">{t('pages.insolvenzticker.empty')}</p>
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <InsolvencyDataGrid
              data={items}
              total={items.length}
              country={country}
              lang={lang}
              locale={locale}
              fmt={fmt}
            />
          )}
        </div>
        {!loading && !error && items.length > 0 && (
          <div className="flex items-center justify-between gap-2 border-t px-3 py-2 shrink-0">
            <span className="text-xs text-muted-foreground">
              {t('pages.insolvenzticker.resultRange', {
                start: page * PAGE_SIZE + 1,
                end: Math.min((page + 1) * PAGE_SIZE, total),
                total,
              })}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-11 lg:h-8 text-xs"
                disabled={page === 0 || loading}
                onClick={() => {
                  logEvent('pagination', `Previous (to page ${page - 1})`, {
                    direction: 'prev',
                    from: page,
                    to: page - 1,
                  });
                  setPage((p) => p - 1);
                }}
              >
                {t('pages.insolvenzticker.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-11 lg:h-8 text-xs"
                disabled={(page + 1) * PAGE_SIZE >= total || loading}
                onClick={() => {
                  logEvent('pagination', `Next (to page ${page + 1})`, {
                    direction: 'next',
                    from: page,
                    to: page + 1,
                  });
                  setPage((p) => p + 1);
                }}
              >
                {t('pages.insolvenzticker.next')}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataGrid Table
// ---------------------------------------------------------------------------

function InsolvencyDataGrid({
  data,
  total,
  country,
  lang,
  locale,
  fmt,
}: {
  data: InsolvencyListItem[];
  total: number;
  country: CompanyCountryCode;
  lang: string;
  locale: Locale;
  fmt: ReturnType<typeof createFormatters>;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const euIds = useMemo(
    () => data.flatMap((row) => row.eu_id ? [row.eu_id] : []),
    [data],
  );
  const logoFor = useCompanyLogos(euIds);
  const [failedLogoUrls, setFailedLogoUrls] = useState<Set<string>>(new Set());

  const columns = useMemo<ColumnDef<InsolvencyListItem>[]>(
    () => [
      {
        accessorKey: 'publish_date',
        header: () => <span>{lang === 'de' ? 'Datum' : 'Date'}</span>,
        meta: { cellClassName: 'overflow-visible text-clip whitespace-nowrap' },
        cell: (info) => {
          const row = info.row.original;
          const publish = row.publish_date;
          const created = row.created_at;
          // If publish_date matches created_at's date part, prefer created_at
          // shown to the minute — gives users a precise timestamp when we
          // actually scraped the row that day.
          const dateParts = publish.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          const currentYear = new Date().getFullYear();
          let fullDate = dateParts
            ? `${dateParts[3]}.${dateParts[2]}.${dateParts[1]}`
            : publish;
          let display = fullDate;
          let title = fullDate;
          if (created && publish && created.slice(0, 10) === publish) {
            const d = new Date(created);
            if (!isNaN(d.getTime())) {
              const pad = (n: number) => String(n).padStart(2, '0');
              const shortDate = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.`;
              const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
              fullDate = `${shortDate}${d.getFullYear()}`;
              title = `${fullDate} ${time}`;
              display = d.getFullYear() === currentYear
                ? `${shortDate} ${time}`
                : fullDate;
            }
          }
          return (
            <span className={`${secondaryTextClass} whitespace-nowrap`} title={title}>
              {display}
            </span>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'company_name',
        // The extra 14px goes to the two money columns, including padding.
        size: 226,
        minSize: 200,
        meta: {
          headerClassName:
            'w-[calc(var(--insolvency-company-width)_+_max(0px,(100%_-_var(--insolvency-table-width))_*_0.6))]!',
        },
        header: () => <span>{lang === 'de' ? 'Unternehmen' : 'Company'}</span>,
        cell: (info) => {
          const row = info.row.original;
          // Build the SEO slug from the canonical_* fields joined off the
          // company table in the backend. Scraped register fields on the
          // insolvency row itself can drift, so the helper returns null when
          // they're missing and we degrade to an unlinked label rather than
          // emit the legacy /company/<euId> form (now 404).
          const href = tryBuildCompanySlugFromCanonical(row, locale);
          const logoUrl = row.eu_id ? logoFor(row.eu_id) : undefined;
          const content = (
            <>
              <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/95 dark:bg-white border border-black/5 dark:border-white/20 shrink-0">
                {logoUrl && !failedLogoUrls.has(logoUrl) ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="h-3.5 w-3.5 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      setFailedLogoUrls((previous) => new Set(previous).add(logoUrl));
                    }}
                  />
                ) : (
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                )}
              </span>
              <span className="truncate block w-full min-w-0" title={row.company_name}>
                {row.company_name}
              </span>
            </>
          );
          return href ? (
            <LocaleLink
              href={href}
              className="flex items-center gap-2 w-full min-w-0 font-medium text-sm text-foreground hover:text-primary hover:underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {content}
            </LocaleLink>
          ) : (
            <span
              className="flex items-center gap-2 w-full min-w-0 font-medium text-sm text-foreground"
            >
              {content}
            </span>
          );
        },
      },
      // Swiss and Austrian notices carry no Aktenzeichen; the company's UID
      // (CHE-…) or Firmenbuchnummer ("FN 359363a") identifies the case.
      ...(country !== 'DE'
        ? [
            {
              id: 'register_id',
              header: () => (
                <span>
                  {country === 'CH'
                    ? t('pages.insolvenzticker.colRegisterId')
                    : COMPANY_COUNTRIES[country].registerShortLabel}
                </span>
              ),
              meta: { cellClassName: 'overflow-visible text-clip whitespace-nowrap' },
              cell: (info: CellContext<InsolvencyListItem, unknown>) => (
                <span
                  className={`${secondaryTextClass} whitespace-nowrap tabular-nums`}
                  title={info.row.original.register_id || '—'}
                >
                  {info.row.original.register_id || '—'}
                </span>
              ),
              size: 140,
            },
          ]
        : []),
      {
        accessorKey: 'category',
        header: () => <span>{t('pages.insolvenzticker.colProcedure')}</span>,
        cell: (info) => {
          const category = info.row.original.category;
          const labelKey = INSOLVENCY_CATEGORY_LABEL_KEYS.get(category);
          return (
            <span
              className={`${secondaryTextClass} truncate block w-full`}
              title={category}
            >
              {labelKey ? t(labelKey) : category}
            </span>
          );
        },
        size: 120,
      },
      {
        // CH rows hold the canton's official name here (code in `canton`).
        accessorKey: 'bundesland',
        header: () => (
          <span>
            {country === 'CH' ? t('pages.insolvenzticker.canton') : 'Bundesland'}
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          const region = (info.getValue() as string | null) || row.canton;
          const label = (country === 'CH' ? swissCantonLabel(region, lang) : region) || '—';
          return (
            <span
              className={`${secondaryTextClass} truncate block w-full`}
              title={label}
            >
              {label}
            </span>
          );
        },
        size: country === 'CH' ? 130 : 160,
      },
      {
        id: 'industry_category',
        header: () => <span>{lang === 'de' ? 'Branche' : 'Industry'}</span>,
        meta: {
          headerClassName:
            'w-[calc(var(--insolvency-industry-width)_+_max(0px,(100%_-_var(--insolvency-table-width))_*_0.4))]!',
        },
        cell: (info) => {
          const row = info.row.original;
          const cat = naceToCategory(row.nace_code);
          const label = cat ? getCategoryLabel(cat, lang) : null;
          return (
            <span
              className={`${secondaryTextClass} truncate block w-full`}
              title={label || '—'}
            >
              {label || '—'}
            </span>
          );
        },
        size: 190,
        minSize: 150,
      },
      {
        accessorKey: 'revenue',
        header: () => <span>{lang === 'de' ? 'Umsatz' : 'Revenue'}</span>,
        meta: {
          headerClassName: 'text-right tabular-nums',
          cellClassName: 'text-right tabular-nums overflow-visible text-clip whitespace-nowrap',
        },
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val == null)
            return <span className="text-muted-foreground text-xs">—</span>;
          const row = info.row.original;
          const amount = fmt.compactCurrency.format(val);
          return (
            <span
              className={`${secondaryTextClass} whitespace-nowrap`}
              title={`${amount}${row.revenue_year ? ` (${row.revenue_year})` : ''}`}
            >
              {amount}
            </span>
          );
        },
        size: 112,
      },
      {
        accessorKey: 'profit',
        header: () => <span>{lang === 'de' ? 'Gewinn' : 'Profit'}</span>,
        meta: {
          headerClassName: 'text-right tabular-nums',
          cellClassName: 'text-right tabular-nums overflow-visible text-clip whitespace-nowrap',
        },
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val == null)
            return <span className="text-muted-foreground text-xs">—</span>;
          const amount = fmt.compactCurrency.format(val);
          return (
            <span
              className={cn(
                secondaryTextClass,
                'whitespace-nowrap',
                val < 0 && 'text-destructive',
              )}
              title={amount}
            >
              {amount}
            </span>
          );
        },
        size: 112,
      },
      {
        // For CH rows the bankruptcy office (Konkursamt) or court; for AT
        // rows the Landesgericht that keeps the Firmenbuch entry.
        accessorKey: 'insolvency_court',
        header: () => (
          <span>
            {country === 'CH'
              ? t('pages.insolvenzticker.colCourtOffice')
              : lang === 'de' ? 'Gericht' : 'Court'}
          </span>
        ),
        cell: (info) => (
          <span
            className={`${secondaryTextClass} truncate block w-full`}
            title={(info.getValue() as string) || '—'}
          >
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 175,
      },
    ],
    [country, lang, locale, fmt, t, logoFor, failedLogoUrls],
  );

  // The mutable TanStack instance is used directly by the grid, outside compiler memoization.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
    },
  });

  return (
    <div className="flex flex-col w-full min-w-0 bg-background overflow-hidden lg:h-full">
      <div className="flex-1 min-h-0 min-w-0 relative overflow-hidden">
        <DataGridContainer className="h-full flex flex-col border-none rounded-none overflow-hidden min-w-0">
          <DataGrid<InsolvencyListItem>
            table={table}
            recordCount={total}
            onRowClick={(row) => {
              const href = tryBuildCompanySlugFromCanonical(row, locale);
              if (href) router.push(href);
            }}
            tableLayout={{
              headerSticky: true,
              headerBorder: true,
              rowBorder: true,
              stripped: false,
              dense: true,
              width: 'fixed',
              columnsResizable: true,
              cellBorder: false,
            }}
            tableClassNames={{
              base: 'w-full min-w-[var(--insolvency-table-width)]',
              header:
                'bg-gray-50/80 dark:bg-zinc-900/90 backdrop-blur-sm text-[10px] font-bold text-gray-500 dark:text-zinc-300 uppercase tracking-widest',
              headerRow: 'h-10',
              body: 'text-gray-700 dark:text-zinc-300 font-sans',
              bodyRow:
                'h-9 hover:bg-blue-50/20 dark:hover:bg-white/5 border-b dark:border-white/10 transition-colors cursor-pointer',
            }}
          >
            <div
              style={{
                // Fixed headers keep their TanStack sizes; only these two
                // columns share spare width, including after a resize.
                '--insolvency-table-width': `${table.getTotalSize()}px`,
                '--insolvency-company-width': `${table.getColumn('company_name')?.getSize()}px`,
                '--insolvency-industry-width': `${table.getColumn('industry_category')?.getSize()}px`,
              } as CSSProperties}
              className="flex-1 min-h-0 min-w-0 overflow-auto pb-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:pb-0 scroll-pb-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px)+0.75rem)] lg:scroll-pb-0 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-zinc-700"
            >
              <DataGridTable />
            </div>
          </DataGrid>
        </DataGridContainer>
      </div>
    </div>
  );
}
